import React, { useEffect, useMemo, useState } from "react";
import { Calendar, CalendarRange } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function getDateRangePreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);

  switch (preset) {
    case "last_7_days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { fromDate: toIsoDate(start), toDate: toIsoDate(end) };
    }
    case "last_30_days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { fromDate: toIsoDate(start), toDate: toIsoDate(end) };
    }
    case "this_week": {
      const start = new Date(today);
      const weekday = start.getDay();
      const mondayOffset = weekday === 0 ? 6 : weekday - 1;
      start.setDate(start.getDate() - mondayOffset);
      return { fromDate: toIsoDate(start), toDate: toIsoDate(end) };
    }
    case "previous_week": {
      const start = new Date(today);
      const weekday = start.getDay();
      const mondayOffset = weekday === 0 ? 6 : weekday - 1;
      start.setDate(start.getDate() - mondayOffset - 7);
      const weekEnd = new Date(start);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return { fromDate: toIsoDate(start), toDate: toIsoDate(weekEnd) };
    }
    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { fromDate: toIsoDate(start), toDate: toIsoDate(end) };
    }
    case "previous_month": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return { fromDate: toIsoDate(start), toDate: toIsoDate(monthEnd) };
    }
    default:
      return { fromDate: "", toDate: "" };
  }
}

const PRESET_GROUPS = [
  {
    title: "Quick",
    items: [
      { id: "last_7_days", label: "Last 7 days" },
      { id: "last_30_days", label: "Last 30 days" },
    ],
  },
  {
    title: "Week",
    items: [
      { id: "this_week", label: "This week" },
      { id: "previous_week", label: "Previous week" },
    ],
  },
  {
    title: "Month",
    items: [
      { id: "this_month", label: "This month" },
      { id: "previous_month", label: "Previous month" },
    ],
  },
];

export function formatDateRangeLabel(fromDate, toDate) {
  if (!fromDate && !toDate) return "All dates";
  const fmt = (iso) => {
    if (!iso) return "…";
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  if (fromDate && toDate) return `${fmt(fromDate)} – ${fmt(toDate)}`;
  if (fromDate) return `From ${fmt(fromDate)}`;
  return `Until ${fmt(toDate)}`;
}

export default function PaymentDateRangeModal({
  open,
  onOpenChange,
  fromDate,
  toDate,
  onApply,
}) {
  const [draftFrom, setDraftFrom] = useState(fromDate || "");
  const [draftTo, setDraftTo] = useState(toDate || "");
  const [activePreset, setActivePreset] = useState(null);

  useEffect(() => {
    if (open) {
      setDraftFrom(fromDate || "");
      setDraftTo(toDate || "");
      setActivePreset(null);
    }
  }, [open, fromDate, toDate]);

  const previewLabel = useMemo(
    () => formatDateRangeLabel(draftFrom, draftTo),
    [draftFrom, draftTo]
  );

  const applyPreset = (presetId) => {
    const range = getDateRangePreset(presetId);
    setDraftFrom(range.fromDate);
    setDraftTo(range.toDate);
    setActivePreset(presetId);
  };

  const handleClear = () => {
    setDraftFrom("");
    setDraftTo("");
    setActivePreset(null);
  };

  const handleApply = () => {
    if (draftFrom && draftTo && draftFrom > draftTo) {
      return;
    }
    onApply({ fromDate: draftFrom, toDate: draftTo });
    onOpenChange(false);
  };

  const rangeInvalid = Boolean(draftFrom && draftTo && draftFrom > draftTo);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg p-0 gap-0 overflow-hidden rounded-xl">
        <DialogHeader className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b bg-white">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <CalendarRange className="w-5 h-5 text-blue-600" />
            Date range
          </DialogTitle>
          <p className="text-sm text-gray-500 font-normal pt-1">
            Choose a preset or pick custom dates. The table updates when you apply.
          </p>
        </DialogHeader>

        <div className="grid sm:grid-cols-[1fr_1.1fr] divide-y sm:divide-y-0 sm:divide-x">
          <div className="p-4 bg-slate-50/80 space-y-4 max-h-[min(60vh,18rem)] sm:max-h-[min(60vh,22rem)] overflow-y-auto">
            {PRESET_GROUPS.map((group) => (
              <div key={group.title}>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                  {group.title}
                </p>
                <div className="flex flex-col gap-1.5">
                  {group.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => applyPreset(item.id)}
                      className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activePreset === item.id
                          ? "bg-blue-600 text-white shadow-sm"
                          : "bg-white border border-gray-200 text-gray-800 hover:border-blue-300 hover:bg-blue-50"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 space-y-4 bg-white">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Custom range
            </p>
            <label className="block text-sm">
              <span className="text-gray-600 font-medium">From</span>
              <input
                type="date"
                value={draftFrom}
                max={draftTo || undefined}
                onChange={(e) => {
                  setDraftFrom(e.target.value);
                  setActivePreset(null);
                }}
                className="mt-1.5 w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-600 font-medium">To</span>
              <input
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => {
                  setDraftTo(e.target.value);
                  setActivePreset(null);
                }}
                className="mt-1.5 w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </label>
            {rangeInvalid && (
              <p className="text-xs text-red-600">End date must be on or after start date.</p>
            )}
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
              <span className="text-gray-500">Preview: </span>
              {previewLabel}
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-5 py-4 border-t bg-gray-50 flex-row justify-between sm:justify-between gap-2 flex-wrap">
          <Button type="button" variant="outline" onClick={handleClear} size="sm">
            Clear range
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} size="sm">
              Cancel
            </Button>
            <Button type="button" onClick={handleApply} disabled={rangeInvalid} size="sm">
              Apply range
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DateRangeFilterButton({ fromDate, toDate, onClick, className = "" }) {
  const label = formatDateRangeLabel(fromDate, toDate);
  const hasRange = Boolean(fromDate || toDate);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 border rounded-lg px-3 py-2 text-sm transition-colors hover:bg-gray-50 ${
        hasRange ? "border-blue-300 bg-blue-50/50 text-blue-900" : "border-gray-300 text-gray-700"
      } ${className}`}
    >
      <Calendar className="w-4 h-4 shrink-0" />
      <span className="font-medium">Date range</span>
      <span className="text-xs text-gray-600 truncate max-w-[11rem]">{label}</span>
    </button>
  );
}
